// ============================================================================
// FinanceSaisiePage (E2b, F-506/M-516) — saisie manuelle ligne a ligne
// ============================================================================
// Tableau editable admin (parite PoC /finance/saisie). Les lignes = pad catalogue
// (placeholders a 0) ; la 1ere saisie d'un placeholder devient un INSERT.
// ADMIN seul ; invalidation finance + indicateurs dashboard apres mutation.

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, SkeletonCard, QueryError, GS_COMPONENTS,
} from '@gs/ui';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useFinanceDetail, useFinanceLinePut } from '../../hooks/useFinance';
import { PeriodPicker } from '../../components/finance/PeriodPicker';
import { FinanceForbidden } from './ResultatsFinanciersPage';
import { formatMonthLong } from './format';

type Draft = {
  amountActual: string;
  amountBudget: string;
  amountPriorYear: string;
};

function parseAmount(raw: string): number | null {
  const n = Number(raw.replace(',', '.').trim());
  return Number.isFinite(n) ? n : null;
}

export default function FinanceSaisiePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const periodParam = searchParams.get('period');
  const detailQ = useFinanceDetail(periodParam, 'tableau', !!user);
  const put = useFinanceLinePut();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const forbidden = detailQ.isError && detailQ.error instanceof ApiForbiddenError;
  const period = detailQ.data?.period ?? null;
  const lines = detailQ.data?.lines ?? [];

  const rows = useMemo(
    () => [...lines].sort((a, b) => {
      if (a.section !== b.section) return a.section === 'revenue' ? -1 : 1;
      return a.sort_order - b.sort_order;
    }),
    [lines],
  );

  function draftFor(line: (typeof lines)[0]): Draft {
    const key = `${line.section}:${line.code}`;
    return drafts[key] ?? {
      amountActual: String(line.amount_actual),
      amountBudget: String(line.amount_budget),
      amountPriorYear: String(line.amount_prior_year),
    };
  }

  function setDraft(line: (typeof lines)[0], patch: Partial<Draft>) {
    const key = `${line.section}:${line.code}`;
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...draftFor(line), ...prev[key], ...patch },
    }));
  }

  async function saveLine(line: typeof lines[0]) {
    if (!period) return;
    const d = draftFor(line);
    const amountActual = parseAmount(d.amountActual);
    const amountBudget = parseAmount(d.amountBudget);
    const amountPriorYear = parseAmount(d.amountPriorYear);
    if (amountActual === null || amountBudget === null || amountPriorYear === null) {
      setSaveError(t('gest:finance.saisie.invalidAmount'));
      return;
    }
    setSavingCode(line.code);
    setSaveError(null);
    try {
      await put.mutateAsync({
        period,
        section: line.section,
        code: line.code,
        label: line.label,
        sortOrder: line.sort_order,
        groupLabel: line.group ?? null,
        amountActual,
        amountBudget,
        amountPriorYear,
      });
    } catch (e) {
      if (!(e instanceof ApiForbiddenError)) {
        setSaveError(e instanceof Error ? e.message : t('gest:finance.saisie.saveError'));
      }
    } finally {
      setSavingCode(null);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:finance.saisie.title')}
        onRefresh={() => void detailQ.refetch()}
        refreshLoading={detailQ.isFetching}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {period ? <PeriodPicker value={period} /> : null}
            <Link to="/resultats-financiers/detail" className={GS_COMPONENTS.btnSecondary}>
              {t('gest:finance.import.backToDetail')}
            </Link>
          </div>
        )}
      />

      {forbidden ? (
        <FinanceForbidden />
      ) : detailQ.isLoading ? (
        <SkeletonCard />
      ) : detailQ.isError || !detailQ.data ? (
        <QueryError message={t('gest:finance.loadError')} onRetry={() => void detailQ.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('gest:finance.saisie.empty.title')}
          description={t('gest:finance.saisie.empty.description')}
        />
      ) : (
        <>
          <p className="text-field text-slate-500 dark:text-slate-400">
            {t('gest:finance.summary.periodLabel', { month: formatMonthLong(detailQ.data.period, i18n.language) })}
          </p>
          {saveError && (
            <p className="text-field text-red-600 dark:text-red-400" role="alert">{saveError}</p>
          )}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <table className="w-full min-w-max border-collapse text-field">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900">
                  <th className="px-3 py-2 text-left">{t('gest:finance.table.description')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.table.actual')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.table.budget')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.table.priorYear')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.saisie.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((line) => {
                  const d = draftFor(line);
                  const busy = savingCode === line.code && put.isPending;
                  return (
                    <tr key={`${line.section}-${line.code}`} className="border-b border-slate-100 dark:border-slate-700">
                      <td className="px-3 py-2">
                        <span className="font-medium text-slate-800 dark:text-slate-100">{line.label}</span>
                        <span className="ml-2 text-meta text-slate-400">{line.code}</span>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={`${GS_COMPONENTS.input} w-28 text-right tabular-nums`}
                          value={d.amountActual}
                          onChange={(e) => setDraft(line, { amountActual: e.target.value })}
                          aria-label={`${line.label} ${t('gest:finance.table.actual')}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={`${GS_COMPONENTS.input} w-28 text-right tabular-nums`}
                          value={d.amountBudget}
                          onChange={(e) => setDraft(line, { amountBudget: e.target.value })}
                          aria-label={`${line.label} ${t('gest:finance.table.budget')}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={`${GS_COMPONENTS.input} w-28 text-right tabular-nums`}
                          value={d.amountPriorYear}
                          onChange={(e) => setDraft(line, { amountPriorYear: e.target.value })}
                          aria-label={`${line.label} ${t('gest:finance.table.priorYear')}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className={GS_COMPONENTS.btnAction}
                          disabled={busy}
                          onClick={() => void saveLine(line)}
                        >
                          {busy ? t('gest:finance.saisie.saving') : t('gest:finance.saisie.save')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
